import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Skeleton from '@mui/material/Skeleton';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IOrderRow } from '../../../Entities';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';
import { countImages } from '../../entry/state/utils';
import { getInitialTab, ImageListTab } from '../state/get-initial-tab';
import { getAdditionalProducts } from '../state/order-row-summary';
import { ImageLoadingSkeleton } from '../../../shared/imageCard/view/ImageLoadingSkeleton';
import { OrderRowHeaderSkeleton } from './OrderRowHeader';
import { ImageGrid, ImagesBody } from './OrderRowImagesLayout';
import { OrderRowImagesSection } from './OrderRowImagesSection';
import { ThumbnailArea, ThumbnailCardFrame } from './ThumbnailCardFrame';

const TabBar = styled(Tabs)(({ theme }) => ({
    width: '100%',
    minHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    '& .MuiTabs-indicator': {
        display: 'none'
    }
}));

const ImageTab = styled(Tab)(({ theme }) => ({
    minHeight: '40px',
    padding: '0 16px',
    textTransform: 'none',
    fontSize: '14px',
    color: theme.palette.primary.contrastText,
    opacity: 1,
    '&.Mui-selected': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.light
    },
    '&:hover': {
        backgroundColor: theme.palette.primary.light
    }
}));

const ImageCountChip = styled(Chip)({
    height: '20px',
    backgroundColor: '#919191',
    color: '#fff',
    fontSize: '12px'
});

type TabLabelProps = {
    title: string;
    imageCount: number;
};

function TabLabel({ title, imageCount }: TabLabelProps) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{title}</span>
            <ImageCountChip label={imageCount} size="small" />
        </Box>
    );
}

type TabPanelProps = {
    tab: ImageListTab;
    currentTab: ImageListTab;
    orderRows: IOrderRow[];
    orderRowsUpdatedAt: number;
    orderId: number;
    photoStatus: ImageEditorPhotoStatus;
    user: AuthenticatedUser;
};

function TabPanel({
    tab,
    currentTab,
    orderRows,
    orderRowsUpdatedAt,
    orderId,
    photoStatus,
    user
}: TabPanelProps) {
    let showPlaceholders = tab === ImageListTab.Unedited;
    let additionalProducts = getAdditionalProducts(photoStatus);
    let [compareShortcutOwnerRowId, setCompareShortcutOwnerRowId] = React.useState<number | null>(
        null
    );

    return (
        <Box
            role="tabpanel"
            hidden={currentTab !== tab}
            id={`image-list-tabpanel-${tab}`}
            aria-labelledby={`image-list-tab-${tab}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px' }}
        >
            {currentTab === tab &&
                orderRows.map((orderRow, index) => (
                    <OrderRowImagesSection
                        key={orderRow.id}
                        orderId={orderId}
                        orderRow={orderRow}
                        showPlaceholders={showPlaceholders}
                        photoStatus={photoStatus}
                        additionalProducts={index === 0 ? additionalProducts : []}
                        orderRowsUpdatedAt={orderRowsUpdatedAt}
                        user={user}
                        ownsCompareShortcut={compareShortcutOwnerRowId === orderRow.id}
                        onClaimCompareShortcut={() => setCompareShortcutOwnerRowId(orderRow.id)}
                    />
                ))}
        </Box>
    );
}

type ImageListTabsProps = {
    orderId: number;
    user: AuthenticatedUser;
    photoStatus: ImageEditorPhotoStatus;
    uneditedOrderRows: IOrderRow[] | null;
    editedOrderRows: IOrderRow[];
    editedOrderRowsUpdatedAt?: number;
};

export function ImageListTabs({
    orderId,
    user,
    photoStatus,
    uneditedOrderRows,
    editedOrderRows,
    editedOrderRowsUpdatedAt = 0
}: ImageListTabsProps) {
    let photographerIsEditor = photoStatus.photographerIsEditor;
    let showUneditedImages = uneditedOrderRows !== null;
    let editedImageCount = countImages(editedOrderRows);
    let [currentTab, setCurrentTab] = React.useState(() =>
        getInitialTab({ showUneditedImages, editedImageCount, photographerIsEditor })
    );

    return (
        <Box component="section">
            <TabBar
                value={currentTab}
                onChange={(event, tab: ImageListTab) => setCurrentTab(tab)}
                aria-label={Translate('view-order/tab/image-processing/legend/image-processing')}
            >
                {showUneditedImages && (
                    <ImageTab
                        value={ImageListTab.Unedited}
                        id={`image-list-tab-${ImageListTab.Unedited}`}
                        aria-controls={`image-list-tabpanel-${ImageListTab.Unedited}`}
                        label={
                            <TabLabel
                                title={Translate('image-processing/page/tab/unedited-images')}
                                imageCount={countImages(uneditedOrderRows)}
                            />
                        }
                    />
                )}
                <ImageTab
                    value={ImageListTab.Edited}
                    id={`image-list-tab-${ImageListTab.Edited}`}
                    aria-controls={`image-list-tabpanel-${ImageListTab.Edited}`}
                    label={
                        <TabLabel
                            title={Translate('image-processing/page/tab/edited-images')}
                            imageCount={editedImageCount}
                        />
                    }
                />
            </TabBar>
            {showUneditedImages && (
                <TabPanel
                    tab={ImageListTab.Unedited}
                    currentTab={currentTab}
                    orderRows={uneditedOrderRows}
                    orderRowsUpdatedAt={0}
                    orderId={orderId}
                    photoStatus={photoStatus}
                    user={user}
                />
            )}
            <TabPanel
                tab={ImageListTab.Edited}
                currentTab={currentTab}
                orderRows={editedOrderRows}
                orderRowsUpdatedAt={editedOrderRowsUpdatedAt}
                orderId={orderId}
                photoStatus={photoStatus}
                user={user}
            />
        </Box>
    );
}

const SKELETON_THUMBNAIL_COUNT = 6;

function TabLabelSkeleton() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Skeleton
                variant="text"
                animation="wave"
                width={110}
                sx={{ fontSize: '14px', bgcolor: 'rgba(255, 255, 255, 0.3)' }}
            />
            <Skeleton
                variant="rounded"
                animation="wave"
                width={28}
                height={20}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}
            />
        </Box>
    );
}

type ImageListTabsSkeletonProps = {
    showUneditedImages: boolean;
};

export function ImageListTabsSkeleton({ showUneditedImages }: ImageListTabsSkeletonProps) {
    return (
        <Box component="section" aria-busy="true">
            <TabBar value={0}>
                {showUneditedImages && <ImageTab value={0} disabled label={<TabLabelSkeleton />} />}
                <ImageTab
                    value={showUneditedImages ? 1 : 0}
                    disabled
                    label={<TabLabelSkeleton />}
                />
            </TabBar>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px' }}>
                <Box component="section">
                    <OrderRowHeaderSkeleton />
                    <ImagesBody>
                        <ImageGrid>
                            {Array.from({ length: SKELETON_THUMBNAIL_COUNT }, (_, index) => (
                                <ThumbnailCardFrame key={index} data-testid="thumbnail-skeleton">
                                    <ThumbnailArea aria-hidden="true">
                                        <ImageLoadingSkeleton />
                                    </ThumbnailArea>
                                </ThumbnailCardFrame>
                            ))}
                        </ImageGrid>
                    </ImagesBody>
                </Box>
            </Box>
        </Box>
    );
}
